import React, { useState } from 'react';
import { Download, Play, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { blogPosts } from '../mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MortgageCalculator from '../components/MortgageCalculator';
import IncomeAnnualisationCalculator from '../components/IncomeAnnualisationCalculator';

const LearnMorePage = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [locationFilter, setLocationFilter] = useState('All');
  const [currentBlogIndex, setCurrentBlogIndex] = useState(0);
  
  const locations = ['All', 'Cairns', 'Melbourne', 'Gold Coast', 'National'];
  
  const filteredBlogs = locationFilter === 'All' 
    ? blogPosts 
    : blogPosts.filter(blog => blog.location === locationFilter);
  
  const blogsPerPage = 3;
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);
  const currentBlogs = filteredBlogs.slice(currentBlogIndex, currentBlogIndex + blogsPerPage);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for signing up for mortgage news!');
    setNewsletterEmail('');
  };

  const nextBlogs = () => {
    if (currentBlogIndex + blogsPerPage < filteredBlogs.length) {
      setCurrentBlogIndex(currentBlogIndex + blogsPerPage);
    }
  };

  const prevBlogs = () => {
    if (currentBlogIndex - blogsPerPage >= 0) {
      setCurrentBlogIndex(currentBlogIndex - blogsPerPage);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Learn More About Finance
            </h1>
            <p className="text-xl text-gray-600">
              Resources and tools to help you make informed financial decisions
            </p>
          </div>
        </div>
      </section>

      {/* Free Budget Planner */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="border-2 hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-3xl" style={{ color: 'rgb(8, 120, 191)' }}>
                  Free Budget Planner
                </CardTitle>
                <CardDescription className="text-lg">
                  Download our comprehensive Excel budget planner to track your income and expenses. You can:
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div>
                    <img 
                      src="Budget_Planner.jpg"
                      alt="Budget planning"
                      className="rounded-lg shadow-lg w-full h-auto"
                    />
                  </div>
                  <div className="space-y-4">
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Track income and expenses on a weekly, fortnightly, monthly, quarterly, or annual frequency
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Calculate and consolidate different rates of income to find out how much that weekly subscription adds up to in a year
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Identify sources of financial stress
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Visualise where your money is going with the chart
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Know if your budget is sustainable
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Easy-to-use Excel format, which can be customised to fit your situation
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Help you calculate borrowing capacity with our responsive mortgage calculator down below
                      </li>
                    </ul>
                    <Button 
                      size="lg" 
                      className="w-full md:w-auto text-white"
                      style={{ backgroundColor: 'rgb(8, 120, 191)' }}
                      onClick={() => window.open('/budget-planner_moneysmart.xls', '_blank')}
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Budget Planner
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mortgage Calculator */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Mortgage Calculator
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Calculate your potential mortgage payments and plan your budget
            </p>
            
            <MortgageCalculator />
          </div>
        </div>
      </section>

      {/* Income Annualisation Calculator */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Income Annualisation Calculator
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Calculate your annual income from year-to-date earnings
            </p>

            <IncomeAnnualisationCalculator />
          </div>
        </div>
      </section>

      {/* First Home Buyer's Grant Video */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              First Home Buyer's Grant Information
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Learn about available grants and assistance programs for first home buyers
            </p>
            
            <Card className="border-2">
              <CardContent className="p-0">
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://www.youtube.com/embed/fpUvEHrzyMU" 
                    title="First Home Buyer's Grant Information"
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                  ></iframe>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Government schemes and grants */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Government Scheme Guides
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Learn about available government schemes and grants
            </p>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-8">
                <Card className="border-2 hover:shadow-lg transition-shadow">
                  <CardHeader className="text-center">
                    <div className="mx-auto mb-4 overflow-hidden bg-gray-100">
                      <img 
                        src="/guides/5deposit.png"
                        alt="Guide for the 5% Government Scheme"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button 
                      size="lg" 
                      className="w-full md:w-auto text-white"
                      style={{ backgroundColor: 'rgb(8, 120, 191)' }}
                      onClick={() => window.open('https://firsthomebuyers.gov.au/sites/default/files/2025-09/Australian%20Government%205%25%20Deposit%20Scheme%20Information%20Guide.pdf', '_blank')}
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Guide (PDF)
                    </Button>
                  </CardHeader>
                </Card>
                <Card className="border-2 hover:shadow-lg transition-shadow">
                  <CardHeader className="text-center">
                    <div className="mx-auto mb-4 overflow-hidden bg-gray-100">
                      <img 
                        src="/guides/fhss.png"
                        alt="Guide for the First Home Super Saver Scheme"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button 
                      size="lg" 
                      className="w-full md:w-auto text-white"
                      style={{ backgroundColor: 'rgb(8, 120, 191)' }}
                      onClick={() => window.open('https://firsthomebuyers.gov.au/sites/default/files/2025-10/Housing%20AU%20FHSS%20Fact%20Sheet%206%20pages%20%2825%20of%2025%29%20-%20Oct%2020_2025.pdf', '_blank')}
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Guide (PDF)
                    </Button>
                  </CardHeader>
                </Card>
                <Card className="border-2 hover:shadow-lg transition-shadow">
                  <CardHeader className="text-center">
                    <div className="mx-auto mb-4 overflow-hidden bg-gray-100">
                      <img 
                        src="/guides/htb.png"
                        alt="Guide for Help To Buy Scheme"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button 
                      size="lg" 
                      className="w-full md:w-auto text-white"
                      style={{ backgroundColor: 'rgb(8, 120, 191)' }}
                      onClick={() => window.open('https://firsthomebuyers.gov.au/HTBCustomerGuide', '_blank')}
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Guide (PDF)
                    </Button>
                  </CardHeader>
                </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Home Buyer's Guide PDF */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="border-2 hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-3xl" style={{ color: 'rgb(8, 120, 191)' }}>
                  Home Buyer's Guide
                </CardTitle>
                <CardDescription className="text-lg">
                  Comprehensive guide to buying your first home in Australia
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div>
                    <img 
                      src="Home_Buyer_Guide.jpg"
                      alt="Home buying guide"
                      className="rounded-lg shadow-lg w-full h-auto"
                    />
                  </div>
                  <div className="space-y-4">
                    <p className="text-gray-700">
                      Our comprehensive Home Buyer's Guide covers everything you need to know about purchasing your first property, including:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Understanding the entire home buying process
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        The documents you need to prepare
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        The right questions to ask your real estate agent
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Government grants and schemes
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        Tips for repaying your mortgage sooner
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        A glossary of terms so you can maintain an active role in the purchase of your home
                      </li>
                    </ul>
                    <Button 
                      size="lg" 
                      className="w-full md:w-auto text-white"
                      style={{ backgroundColor: 'rgb(8, 120, 191)' }}
                      onClick={() => window.open('https://img1.wsimg.com/blobby/go/c3b8fe5c-1af2-4560-bb9f-50b6450b0d49/downloads/6354b7ce-2dfc-469f-9206-fb9d721515a2/Home%20Buyer%20Essentials-min.pdf?ver=1770765230528', '_blank')}
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Guide (PDF)
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
  {/*    
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-6" style={{ color: 'rgb(8, 120, 191)' }}>
              Mortgage & Finance Blogs
            </h2>
            <p className="text-lg text-gray-600 text-center mb-8">
              Stay informed with our latest insights and tips
            </p>
            
            <div className="flex items-center justify-center mb-8 space-x-2 flex-wrap gap-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <span className="text-gray-600 font-medium">Filter by location:</span>
              {locations.map((location) => (
                <Button
                  key={location}
                  variant={locationFilter === location ? 'default' : 'outline'}
                  onClick={() => {
                    setLocationFilter(location);
                    setCurrentBlogIndex(0);
                  }}
                  style={locationFilter === location ? { backgroundColor: 'rgb(8, 120, 191)' } : { borderColor: 'rgb(8, 120, 191)', color: 'rgb(8, 120, 191)' }}
                  className={locationFilter === location ? 'text-white' : ''}
                >
                  {location}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {currentBlogs.map((blog) => (
                <Card key={blog.id} className="border-2 hover:shadow-xl transition-shadow overflow-hidden">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span 
                        className="text-xs font-semibold px-3 py-1 rounded-full"
                        style={{ backgroundColor: 'rgb(8, 120, 191)', color: 'white' }}
                      >
                        {blog.location}
                      </span>
                      <span className="text-xs text-gray-500">{blog.date}</span>
                    </div>
                    <CardTitle className="text-xl">{blog.title}</CardTitle>
                    <CardDescription className="text-base">
                      {blog.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      style={{ borderColor: 'rgb(8, 120, 191)', color: 'rgb(8, 120, 191)' }}
                      onClick={() => window.open(blog.link || '#', '_blank')}
                    >
                      Read More
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex items-center justify-center space-x-4">
              <Button
                variant="outline"
                onClick={prevBlogs}
                disabled={currentBlogIndex === 0}
                style={{ borderColor: 'rgb(8, 120, 191)', color: 'rgb(8, 120, 191)' }}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              
              <span className="text-gray-600">
                Page {Math.floor(currentBlogIndex / blogsPerPage) + 1} of {totalPages}
              </span>
              
              <Button
                variant="outline"
                onClick={nextBlogs}
                disabled={currentBlogIndex + blogsPerPage >= filteredBlogs.length}
                style={{ borderColor: 'rgb(8, 120, 191)', color: 'rgb(8, 120, 191)' }}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            <p className="text-center text-gray-500 mt-4">
              Showing {currentBlogs.length} of {filteredBlogs.length} posts
            </p>
          </div>
        </div>
      </section>

       Newsletter Signup - Commented out for now
      <section className="py-16 bg-gradient-to-r from-[#3e5fb5] to-blue-600">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="border-0 bg-white/10 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-3xl text-white text-center">
                  Sign Up for Mortgage News
                </CardTitle>
                <CardDescription className="text-blue-100 text-center text-lg">
                  Get the latest updates on mortgage rates, market trends, and finance tips
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="newsletter-email" className="text-white">Email Address</Label>
                    <Input
                      id="newsletter-email"
                      type="email"
                      required
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="mt-2 bg-white"
                    />
                  </div>
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-white hover:bg-gray-100"
                    style={{ color: 'rgb(8, 120, 191)' }}
                  >
                    Subscribe Now
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section> 
    */}

      <Footer />
    </div>
  );
};

export default LearnMorePage;
